import React from 'react';

// Helper component to render formatted inline content
const FormattedText = ({ content }: { content: string }) => (
  <span dangerouslySetInnerHTML={{ __html: formatInlineElements(content) }} />
);

// Determine line type and extract content
interface LineInfo {
  type: 'h1' | 'h2' | 'h3' | 'bullet' | 'text' | 'empty';
  content?: string;
}

const getLineType = (line: string): LineInfo => {
  if (line.startsWith('# ')) return { type: 'h1', content: line.slice(2) };
  if (line.startsWith('## ')) return { type: 'h2', content: line.slice(3) };
  if (line.startsWith('### ')) return { type: 'h3', content: line.slice(4) };
  if (line.startsWith('- ')) return { type: 'bullet', content: line.slice(2) };
  if (line) return { type: 'text', content: line };
  return { type: 'empty' };
};

// Lightweight markdown parsing for basic formatting
export const parseBasicMarkdown = (text: string): React.JSX.Element[] => {
  // Split by lines to handle lists and headings
  const lines = text.split('\n');
  const elements: React.JSX.Element[] = [];
  let currentParagraph: string[] = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const content = currentParagraph.join(' ');
      elements.push(
        <p key={elements.length} className="text-base leading-6 mb-2 last:mb-0">
          <FormattedText content={content} />
        </p>
      );
      currentParagraph = [];
    }
  };

  const pushElement = (element: React.JSX.Element) => {
    elements.push(element);
  };

  lines.forEach((line: string) => {
    const { type, content = '' } = getLineType(line.trim());

    switch (type) {
      case 'h1':
        flushParagraph();
        pushElement(
          <h4 key={elements.length} className="text-xl mt-6 mb-3 font-semibold">
            <FormattedText content={content} />
          </h4>
        );
        break;

      case 'h2':
        flushParagraph();
        pushElement(
          <h5 key={elements.length} className="text-lg mt-5 mb-2 first:mt-0 font-semibold">
            <FormattedText content={content} />
          </h5>
        );
        break;

      case 'h3':
        flushParagraph();
        pushElement(
          <h6 key={elements.length} className="text-lg mt-5 mb-2 first:mt-0 font-semibold">
            <FormattedText content={content} />
          </h6>
        );
        break;

      case 'bullet':
        flushParagraph();
        pushElement(
          <div key={elements.length} className="flex gap-2 my-2 text-base">
            <span className="text-black flex-shrink-0 mt-1">•</span>
            <FormattedText content={content} />
          </div>
        );
        break;

      case 'text':
        currentParagraph.push(content);
        break;

      case 'empty':
        flushParagraph();
        // if (elements.length > 0) {
        //   pushElement(<br key={elements.length} />);
        // }
        break;
    }
  });

  flushParagraph();
  return elements;
};

// Format inline elements like links and bold text
const formatInlineElements = (text: string): string => {
  let formatted = text;

  // Links: [text](url)
  formatted = formatted.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-black underline hover:no-underline">$1</a>'
  );

  // Bold: **text**
  formatted = formatted.replace(
    /\*\*([^*]+)\*\*/g,
    '<strong class="font-bold">$1</strong>'
  );

  // Italic: *text*
  formatted = formatted.replace(
    /\*([^*]+)\*/g,
    '<em>$1</em>'
  );

  return formatted;
};

